import type { MeResponse } from '@dbrain/shared';
import { Avatar, Icons, Menu, MenuItem, MenuSeparator, toneOf, type IconComponent } from '@dbrain/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation, useNavigate } from 'react-router';
import { api } from '../../api/client.js';
import { keys } from '../../api/hooks.js';
import { useCapabilities, type Experience } from '../../experience.js';
import { SidebarHistory } from '../sessions/SessionSidebar.js';
import { settingsLink } from '../settings/links.js';
import { roleLabel } from '@dbrain/app-core/role';

/**
 * The left menu (docs/design/mockups/CalismaAlanlari.dc.html): the
 * organization, "Yeni sohbet", the main places, management for admins, the
 * history by day with status dots, and the user with their role at the
 * bottom. Collapsed it becomes a 72 px rail of icons (CalismaAlani.dc.html);
 * on a phone it slides in as a drawer.
 */
export type SidebarMode = 'expanded' | 'rail';

// Phase 16a: a person's own account owns its space, but has nothing to manage (no organization)
const isAdmin = (me: MeResponse, organization: boolean) => organization && (me.tenant.role === 'owner' || me.tenant.role === 'admin');

function NavItem({ to, icon: Icon, label, end, rail, testId, onNavigate }: { to: string; icon: IconComponent; label: string; end?: boolean; rail: boolean; testId?: string; onNavigate?: () => void }) {
  return (
    <NavLink to={to} end={end} className="nav-item" aria-label={rail ? label : undefined} title={rail ? label : undefined} data-testid={testId} onClick={onNavigate}>
      <Icon size={rail ? 19 : 18} />
      {rail ? null : <span className="nav-item__label">{label}</span>}
    </NavLink>
  );
}

function TenantName({ me, rail }: { me: MeResponse; rail: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const switchTenant = useMutation({
    mutationFn: (tenantId: string) => api<MeResponse>('POST', '/api/session/tenant', { tenantId }),
    onSuccess: (data) => {
      queryClient.clear();
      queryClient.setQueryData(keys.me, data);
      void navigate('/');
    },
  });
  if (rail) return null;
  const name = me.tenant.kind === 'personal' ? t('shell.personalSpace', { name: me.user.displayName }) : me.tenant.name;
  const mark = (
    <span className="sidebar__mark" aria-hidden="true">
      {(name.trim()[0] ?? 'O').toLocaleLowerCase('tr')}
    </span>
  );
  if (me.memberships.length < 2) {
    return (
      <span className="sidebar__tenant">
        {mark}
        <span data-testid="tenant-name">{name}</span>
      </span>
    );
  }
  return (
    <Menu
      className="sidebar__tenant-menu"
      triggerClassName="sidebar__tenant sidebar__tenant--switch"
      label={t('shell.switchTenant')}
      data-testid="tenant-switcher"
      trigger={
        <>
          {mark}
          <span data-testid="tenant-name">{name}</span>
          <Icons.chevronDown size={14} />
        </>
      }
    >
      {me.memberships.map((m) => (
        <MenuItem
          key={m.tenantId}
          icon={m.tenantId === me.tenant.id ? <Icons.check /> : <Icons.organization />}
          onClick={() => m.tenantId !== me.tenant.id && switchTenant.mutate(m.tenantId)}
          aria-current={m.tenantId === me.tenant.id ? 'true' : undefined}
          data-testid="tenant-option"
        >
          {m.kind === 'personal' ? t('shell.personalSpace', { name: me.user.displayName }) : m.name}
        </MenuItem>
      ))}
    </Menu>
  );
}

/** The user at the bottom: their name and role; the row opens the account menu, the button the settings. */
function UserFoot({ me, rail, onLogout, loggingOut }: { me: MeResponse; rail: boolean; onLogout: () => void; loggingOut: boolean }) {
  const { t } = useTranslation();
  const location = useLocation();
  const capabilities = useCapabilities();
  const to = (section: string) => settingsLink(section, location);
  const avatar = <Avatar name={me.user.displayName} tone={toneOf(me.user.id)} />;
  // D-183: a team admin from the directory is "Ekip yöneticisi · Hukuk", not "Üye"; Phase 16a: a person's own
  // account has no role, its plan stands there ("Ücretsiz paket", "Pro")
  const role = me.plan
    ? t(me.plan.id === 'pro' ? 'b2c.role.pro' : 'b2c.role.free')
    : roleLabel(me, {
        owner: t('roles.tenant.owner'),
        admin: t('roles.tenant.admin'),
        domainAdmin: t('roles.domainAdmin'),
        member: t('roles.tenant.member'),
        teams: (count) => t('roles.teams', { count }),
      });
  const menu = (
    <Menu
      direction="up"
      align="left"
      // the name and role the row shows come first in its name (WCAG 2.5.3)
      label={`${me.user.displayName}, ${role} — ${t('shell.settingsMenu')}`}
      triggerClassName={rail ? 'sidebar__user sidebar__user--rail' : 'sidebar__user'}
      className="sidebar__user-menu"
      panelClassName="sidebar__settings-panel"
      data-testid="settings-menu"
      trigger={
        rail ? (
          avatar
        ) : (
          <>
            {avatar}
            <span className="sidebar__user-text">
              <span className="sidebar__user-name">{me.user.displayName}</span>
              <span className="sidebar__user-role" data-testid="sidebar-role">
                {role}
              </span>
            </span>
          </>
        )
      }
    >
      <Link className="ui-menu-item" {...to('general')}>
        <Icons.settings />
        {t('settingsDialog.sections.general')}
      </Link>
      <Link className="ui-menu-item" {...to('account')}>
        <Icons.user />
        {t('settingsDialog.sections.account')}
      </Link>
      <Link className="ui-menu-item" {...to('notifications')}>
        <Icons.bell />
        {t('settingsDialog.sections.notifications')}
      </Link>
      {isAdmin(me, capabilities.organization) ? (
        <Link className="ui-menu-item" to="/admin/audit">
          <Icons.audit />
          {t('admin.sections.audit')}
        </Link>
      ) : null}
      <MenuSeparator />
      <MenuItem danger icon={<Icons.logout />} onClick={onLogout} disabled={loggingOut} data-testid="logout">
        {t('nav.logout')}
      </MenuItem>
    </Menu>
  );
  if (rail) return <div className="sidebar__foot sidebar__foot--rail">{menu}</div>;
  return (
    <div className="sidebar__foot">
      {menu}
      <Link className="ui-icon-button" aria-label={t('shell.settings')} title={t('shell.settings')} {...to('general')} data-testid="nav-settings">
        <Icons.sliders size={18} />
      </Link>
    </div>
  );
}

export function Sidebar({
  me,
  experience,
  mode,
  onToggle,
  onNavigate,
  onLogout,
  loggingOut,
  drawer,
}: {
  me: MeResponse;
  experience: Experience;
  mode: SidebarMode;
  onToggle: () => void;
  onNavigate?: () => void;
  onLogout: () => void;
  loggingOut: boolean;
  /** shown as the phone drawer: always expanded, with a close button */
  drawer?: boolean;
}) {
  const { t } = useTranslation();
  const capabilities = useCapabilities();
  const rail = mode === 'rail' && !drawer;
  // Yönetim: organization admins, and domain admins for their domains (Phase 13d-2, D-101)
  const admin = isAdmin(me, capabilities.organization) || Boolean(me.domains?.some((d) => d.admin));
  // D-135: a kurumsal installation is one company, managed under Yönetim; Platform is bireysel's operators'
  const platform = me.platformOperator && !capabilities.singleCompany;
  const toggleLabel = drawer ? t('shell.closeMenu') : rail ? t('shell.expand') : t('shell.collapse');
  const newLabel = t('shell.newConversation');

  return (
    <aside className={`sidebar sidebar--${rail ? 'rail' : 'expanded'}`} aria-label={t('nav.mainNav')} data-testid="sidebar">
      <div className="sidebar__head">
        <TenantName me={me} rail={rail} />
        <button type="button" className="ui-icon-button" aria-label={toggleLabel} title={toggleLabel} onClick={onToggle} data-testid="sidebar-toggle" aria-expanded={drawer ? true : !rail}>
          {drawer ? <Icons.close /> : <Icons.sidebar />}
        </button>
      </div>
      {/* a new conversation starts on the home page's box */}
      <Link
        to="/"
        className={rail ? 'sidebar__new sidebar__new--rail' : 'sidebar__new'}
        aria-label={rail ? newLabel : undefined}
        title={rail ? newLabel : undefined}
        onClick={onNavigate}
        data-testid={rail ? 'rail-new-conversation' : 'sidebar-new-conversation'}
      >
        <Icons.plus size={rail ? 18 : 16} />
        {rail ? null : newLabel}
      </Link>

      <div className="sidebar__scroll">
        <nav className="sidebar__group" aria-label={t('shell.places')}>
          <NavItem to="/" end icon={Icons.home} label={t('shell.home')} rail={rail} testId="nav-home" onNavigate={onNavigate} />
          <NavItem to="/workspaces" icon={Icons.grid} label={t('shell.workspaces')} rail={rail} testId="nav-workspaces" onNavigate={onNavigate} />
          <NavItem to="/skills" icon={Icons.skills} label={t('shell.skills')} rail={rail} testId="nav-skills" onNavigate={onNavigate} />
          <NavItem to="/connectors" icon={Icons.connections} label={t('shell.connections')} rail={rail} testId="nav-connectors" onNavigate={onNavigate} />
          <NavItem to="/documents" icon={Icons.documents} label={t('shell.documents')} rail={rail} testId="nav-documents" onNavigate={onNavigate} />
          {/* management is one place for admins; its pages have their own sections */}
          {admin ? <NavItem to="/admin" icon={Icons.access} label={t('shell.management')} rail={rail} testId="nav-admin" onNavigate={onNavigate} /> : null}
          {platform ? <NavItem to="/platform" icon={Icons.platform} label={t('shell.platform')} rail={rail} testId="nav-platform" onNavigate={onNavigate} /> : null}
        </nav>

        {rail ? null : <SidebarHistory experience={experience} onNavigate={onNavigate} />}
      </div>

      <UserFoot me={me} rail={rail} onLogout={onLogout} loggingOut={loggingOut} />
    </aside>
  );
}
